import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIS } from "../../../../utils/APIS";
import { GET, POST, PUT, DELETE } from "../../../../utils/Methods";
import { IDS } from "../../../../utils/IDS";

// Wallet_History - Get All (Read)
export const GetAll = createAsyncThunk(
  "UserManagement/role/get-all",
  async (filter, { rejectWithValue }) => {
    try {
      const res = await GET(
        `${APIS.UserManagement.Users}?search=${filter?.search || ""}&page=${
          filter?.page || IDS.PAGE
        }&per_page=${filter?.per_page || IDS.PERPAGE}`
      );
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const GetSingle = createAsyncThunk(
  "UserManagement/role/get",
  async (id, { rejectWithValue }) => {
    try {
      const res = await GET(`${APIS.UserManagement.Users}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const Update = createAsyncThunk(
  "UserManagement/role/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await PUT(`${APIS.UserManagement.Users}/${id}`, data);
      return res;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Add money to user wallet
export const AddMoney = createAsyncThunk(
  "UserManagement/wallet/add-money",
  async ({ id, amount }, { rejectWithValue }) => {
    try {
      const res = await POST(`${APIS.UserManagement.Users}/${id}/add-money`, {
        amount,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Remove money from user wallet
export const RemoveMoney = createAsyncThunk(
  "UserManagement/wallet/remove-money",
  async ({ id, amount }, { rejectWithValue }) => {
    try {
      const res = await POST(
        `${APIS.UserManagement.Users}/${id}/remove-money`,
        { amount }
      );
      return res;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Country - Status
export const StatusUser = createAsyncThunk(
  "masters/country/status",
  async (id, { rejectWithValue }) => {
    try {
      const res = await POST(`${APIS.UserManagement.Users}/${id}`);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const Slice = createSlice({
  name: "user",
  initialState: {
    data: [],
    current_page: IDS.PAGE,
    total_pages: IDS.TOTALPAGES,
    per_page: IDS.PERPAGE,
    total: IDS.TOTAL,
    status: IDS.SLICESTATUS.Idle,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handling all actions for GetSingle, Create, Update, Delete
      .addCase(GetAll.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetAll.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        state.data = action.payload?.data?.data || [];
        state.current_page = action.payload?.data?.current_page || IDS.PAGE;
        state.total_pages = action.payload?.data?.total_pages || IDS.TOTALPAGES;
        state.per_page = action.payload?.data?.per_page || IDS.PERPAGE;
        state.total = action.payload?.data?.total || IDS.TOTAL;
      })
      .addCase(GetAll.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(GetSingle.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(GetSingle.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
      })
      .addCase(GetSingle.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(Update.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(Update.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        const index = state.data.findIndex(
          (country) => country.id === action.payload?.data?.id
        );
        if (index !== -1) {
          state.data[index] = action.payload.data;
        }
      })
      .addCase(Update.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      .addCase(StatusUser.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(StatusUser.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // state.data = action.payload;
        state.data = state.data.filter(
          (country) => country.id !== action.payload.id
        ); // Remove the country with the deleted id
      })
      .addCase(StatusUser.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      // AddMoney
      .addCase(AddMoney.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(AddMoney.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // Update wallet amount in state if needed
        const index = state.data.findIndex(
          (user) => user.id === action.meta.arg.id
        );
        if (index !== -1 && state.data[index].User_Wallet) {
          state.data[index].User_Wallet.amount =
            Number(state.data[index].User_Wallet.amount) +
            Number(action.meta.arg.amount);
        }
      })
      .addCase(AddMoney.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      })

      // RemoveMoney
      .addCase(RemoveMoney.pending, (state) => {
        state.status = IDS.SLICESTATUS.Loading;
      })
      .addCase(RemoveMoney.fulfilled, (state, action) => {
        state.status = IDS.SLICESTATUS.Succeeded;
        // Update wallet amount in state if needed
        const index = state.data.findIndex(
          (user) => user.id === action.meta.arg.id
        );
        if (index !== -1 && state.data[index].User_Wallet) {
          state.data[index].User_Wallet.amount =
            Number(state.data[index].User_Wallet.amount) -
            Number(action.meta.arg.amount);
        }
      })
      .addCase(RemoveMoney.rejected, (state, action) => {
        state.status = IDS.SLICESTATUS.Failed;
        state.error = action.payload;
      });
  },
});

// Export actions (if needed in components)
export const {} = Slice.actions;

export default Slice.reducer;
